import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { setSettings } from "./CalcSettingsSlice";

const initialState = {
    allValues: [],
    calibration: { }, // values come from the downloaded scan
    dataFilters: { }, // values come from the downloaded scan
    endDate: null,
    filterTrace: { },
    filteredY: {machine: [], motor: []},
    filteredZ: {machine: [], motor: []},
    id: null,
    rangeY: {machine: [], motor: []},
    rangeZ: {machine: [], motor: []},
    rangeRadius: {machine: [], motor: []},
    ranges: {machine: {start: -1, end: -1}, motor: {start: -1, end: -1}},
    rawData: [],
    shim: { },
    summary: { },
    RadiusValues: [],
    YValues: [],
    ZValues: [],
}

export const loadScan = createAsyncThunk("get/loadScan", async(args, {dispatch, getState, rejectWithValue}) => {
    console.log(`loadScan...start - rawData length: ${args.scan.rawData.length}`);
    let tempState = JSON.parse(JSON.stringify(initialState));
    tempState.id = args.id;
    tempState.endDate = args.scan.end;
    tempState.rawData = args.scan.rawData;
    tempState.allValues = JSON.parse(JSON.stringify(args.scan.rawData));
    tempState.rangeY = {machine: [], motor: []};
    tempState.rangeZ = {machine: [], motor: []};
    tempState.rangeRadius = {machine: [], motor: []};
    tempState.ranges = {machine: {start: -1, end: -1}, motor: {start: -1, end: -1}};
    tempState.filteredY = {machine: [], motor: []};
    tempState.filteredZ = {machine: [], motor: []};
    tempState.calibration = args.scan.calibration;
    tempState.dataFilters = args.scan.dataFilters;
    tempState.shim = args.scan.shim;
    tempState.summary = args.scan.summary;
    tempState.filterTrace = args.scan.filterTrace;

    console.log("trace: ", tempState.filterTrace);

    dispatch(setSettings({
        calibration: args.scan.calibration,
        dataFilters: args.scan.dataFilters,
        info: args.scan.info,
    }));

    // all data - reassign for use
    let threshold = (process.env.REACT_APP_POINT_THRESHOLD) ? process.env.REACT_APP_POINT_THRESHOLD : 1000;
    let pointCountDisplayed = (process.env.REACT_APP_POINTS_COUNT_DISPLAYED) ? process.env.REACT_APP_POINTS_COUNT_DISPLAYED : 100;
    let step = (tempState.allValues.length > threshold) ? Math.floor(tempState.allValues.length / pointCountDisplayed) : 1;
    console.log(`loadScan - points after nr : ${tempState.allValues.length}`);
    console.log(`loadScan - populate y,z,radius arrays - threshold: ${threshold}; point count: ${pointCountDisplayed}; step: ${step}`);
    for(let i=0;i< tempState.allValues.length;i+=step){
        let data = tempState.allValues[i];
        
        tempState.YValues.push({x:data.x,y:data.y,index: i});
        tempState.ZValues.push({x:data.x,y:data.z,index: i});
        //tempState.RadiusValues.push({x:data.x,y:data.radius,radius:data.radius, index:i});
    }

    // when more than a thousand points reduce the number of points displayed
    step = (tempState.filterTrace.machinePoints.length > threshold) ? Math.floor(tempState.filterTrace.machinePoints.length / pointCountDisplayed) : 1;
    for(let i=0;i<tempState.filterTrace.machinePoints.length;i+=step) {
        let point = tempState.filterTrace.machinePoints[i];
        tempState.filteredY.machine.push({x:point.x, y:point.y, index: i});
        tempState.filteredZ.machine.push({x:point.x, y:point.z, index: i});
    }    

    step = (tempState.filterTrace.motorPoints.length > threshold) ? Math.floor(tempState.filterTrace.motorPoints.length / pointCountDisplayed) : 1;
    for(let i=0;i<tempState.filterTrace.motorPoints.length;i+=step) {
        let point = tempState.filterTrace.motorPoints[i];
        tempState.filteredY.motor.push({x:point.x, y:point.y, index: i});
        tempState.filteredZ.motor.push({x:point.x, y:point.z, index: i});
    }    

    // set range values based on previous state
    let ranges = [];
    ranges.push({start: args.scan.info.machineStart, end: args.scan.info.machineStop});
    ranges.push({start: args.scan.info.motorStart, end: args.scan.info.motorStop});
    for(let r=0;r<ranges.length;r++) {
        for(let i=0;i<tempState.YValues.length;i++) {
            if(tempState.YValues[i].x >= Math.ceil(ranges[r].start) && tempState.YValues[i].x <= Math.floor(ranges[r].end)) {
                // both the dataXY and dataXZ are the same length
                let dataType = tempState.YValues[i].x < args.scan.info.motorStart ? "machine" : "motor";
                tempState.rangeY[dataType].push({...tempState.YValues[i]});
                tempState.rangeZ[dataType].push({...tempState.ZValues[i]});
                //tempState.rangeRadius[dataType].push({...tempState.RadiusValues[i]});
            }
        }
    }

    console.log("loadScan - complete");
    return tempState;
});


export const LaserDataSlice = createSlice({
    name:'laserData',
    initialState,
    reducers: {
        updateRanges: (state, action) => {
            let rangeY = JSON.parse(JSON.stringify(initialState.rangeY));
            let rangeZ = JSON.parse(JSON.stringify(initialState.rangeZ));
            let ranges = [];
            ranges.push({start: action.payload.info.machineStart, end: action.payload.info.machineStop});
            ranges.push({start: action.payload.info.motorStart, end: action.payload.info.motorStop});
            for(let r=0;r<ranges.length;r++) {
                for(let i=0;i<state.YValues.length;i++) {
                    if(state.YValues[i].x >= Math.ceil(ranges[r].start) && state.YValues[i].x <= Math.floor(ranges[r].end)) {
                        // both the dataXY and dataXZ are the same length
                        let dataType = state.YValues[i].x < action.payload.info.motorStart ? "machine" : "motor";
                        rangeY[dataType].push({...state.YValues[i]});
                        rangeZ[dataType].push({...state.ZValues[i]});
                        //tempState.rangeRadius[dataType].push({...tempState.RadiusValues[i]});
                    }
                }
            }

            return {    
                ...state,
                rangeY: rangeY,
                rangeZ: rangeZ
            }
        },
        resetData: (state, action) => initialState,
    },
    extraReducers(builder) {
        builder
            .addCase(loadScan.fulfilled, (state, action) => {
                return {
                    ...action.payload,
                }
            })
    }
})

// public exports
export const { resetData, updateRanges } = LaserDataSlice.actions

export default LaserDataSlice.reducer